import { formatDate } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AnimationOptions } from 'ngx-lottie';
import { forkJoin, of, EMPTY } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppSettings } from 'src/app/app-settings';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { BondsServices } from 'src/app/services/bonds.service';
import { EventosService } from 'src/app/services/eventos.service';
import { ShoppingCartService } from 'src/app/services/shopping-cart.service';
import { TicketsService } from 'src/app/services/tickets.service';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { LoginComponent } from '../../shared/login/login.component';
import { SeatsPanelComponent } from '../seats-panel/seats-panel.component';
import { PassSelectDialogComponent } from '../../passes/pass-select-dialog/pass-select-dialog.component';
import { HoursDatesEventComponent } from '../hours-dates-event/hours-dates-event.component';
import { PassSeat, SingleSeatPassService } from 'src/app/services/single-seat-pass.service';
import { fixedPassFromLabel, isFixedPricePass } from 'src/app/utils/fixed-price-pass.util';

@Component({
  selector: 'app-info-abonos',
  templateUrl: './info-abonos.component.html',
  styleUrls: ['./info-abonos.component.scss']
})
export class InfoAbonosComponent implements OnInit {

  abono!: any;
  event: any;
  sessions: any;
  loaded: boolean = false;
  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';
  listEvents: any[] = [];

  loadingLottie: AnimationOptions = {
    path: 'assets/lotties/loading.json',
  };

  ticketsSelected = [];
  buysBuySelect: any[] = [];
  priceTotal: number = 0;
  openingMap: boolean = false;
  totemActive: boolean = AppSettings.ENV_TOTEM;
  // Los dos siguientes solo se activan en el tótem (ver openSelectEvent).
  // Candado de la cadena "clic en evento -> selector de sesión -> mapa de
  // butacas": se mantiene hasta que se cierra el último diálogo de la cadena.
  selectingEvent: boolean = false;
  // Solo para el spinner: true mientras se carga el evento o el mapa, false en
  // cuanto hay un diálogo en pantalla.
  loadingSelection: boolean = false;
  ticketsLength: number = 0;

  // Compra en butaca única: `passQuantity` butacas, las mismas en todos los
  // eventos; el back da el desglose y resuelve la entrada de cada evento. Es
  // obligatoria si el abono trae `single_seat` (no se recorre evento a evento)
  // y, en el resto de abonos de eventos específicos, se ofrece cuando la
  // butaca elegida en el primer evento está libre en todos.
  passQuantity: number = 1;
  passSeats: PassSeat[] = [];
  passQuote: any = null;
  // El comprador ha aceptado llevarse la misma butaca a todos los eventos
  sameSeatChosen: boolean = false;
  // Evento en cuyo mapa se eligieron `passSeats` (null = mapa del abono)
  passSeatsEventId: number | null = null;

  /** El abono solo se vende en butaca única */
  get singleSeatRequired(): boolean {
    return !!this.abono?.single_seat;
  }

  /** La compra en curso es en butaca única: obligatoria, o aceptada al ofrecérsela */
  get isSingleSeat(): boolean {
    return this.singleSeatRequired || this.sameSeatChosen;
  }

  /** ¿Está ya todo elegido para poder añadir el abono al carrito? */
  get readyToPay(): boolean {
    return this.isSingleSeat ? !!this.passQuote : this.ticketsLength >= this.minTicketsRequired;
  }

  get minTicketsRequired(): number {
    return this.abono?.min_events || this.listEvents.length;
  }

  get isFlexible(): boolean {
    return this.abono?.min_events && this.abono.min_events < this.listEvents.length;
  }

  getDiscountText(): string {
    if (!this.abono) return '';
    return this.abono.discount_mode == 'amount'
      ? ` de ${this.abono.discount_value}€`
      : ` del ${Math.trunc(this.abono.discount_value)}%`;
  }

  /** Abono de precio fijo por evento y nivel: no hay %, cada entrada tiene su precio */
  get isFixedPrices(): boolean {
    return isFixedPricePass(this.abono);
  }

  get fixedPriceLabel(): string {
    return fixedPassFromLabel(this.abono).toLowerCase();
  }

  /** Lo elegido hasta ahora; con precio fijo es lo que costará el abono */
  get selectionsTotal(): number {
    return this.ticketsSelected.reduce((sum: number, sel: any) => sum + (+sel.price || 0), 0);
  }

  constructor(@Optional() private dialogRef: MatDialogRef<InfoAbonosComponent>, private dialog: MatDialog, private shoppingService: ShoppingCartService,
    private router: Router, private ticketService: TicketsService, @Optional() @Inject(MAT_DIALOG_DATA) public data, private eventService: EventosService,
    private auth: AuthenticationService, private bondsService: BondsServices,
    private singleSeatPassService: SingleSeatPassService
  ) { }

  ngOnInit(): void {
    if (!this.data?.event) return;

    this.event = this.data.event;
    this.sessions = this.data.sessions;

    const companyId = this.event?.company?.id || this.event?.id_company;
    if (!companyId) {
      this.showNoPassError();
      return;
    }

    // 1. Get all pass-type discounts for this company
    this.bondsService.getBondsByCompany(companyId).pipe(
      switchMap((res) => {
        const discounts = (res?.discounts || res?.data || [])
          .filter((d: any) => {
            if (d.rule_type !== 'pass' || !d.active) return false;
            const channels: number[] = d.allowed_channels || [];
            if (channels.length > 0) return channels.some((c: number) => [1, 4, 5].includes(c));
            return true;
          });

        if (discounts.length === 0) {
          this.showNoPassError();
            return EMPTY;
          }

          // 2. Fetch full detail of each discount to get pass_items
          return forkJoin(
            discounts.map((d: any) =>
              this.bondsService.getPassById(d.id).pipe(
                map((r) => r?.discount || r?.data || r),
                catchError(() => of(null))
              )
            )
          );
        })
      ).subscribe({
        next: (fullDiscounts: any[]) => {
          // 3. Find the discount that contains this event
          const discount = fullDiscounts
            .filter((d) => !!d)
            .find((d: any) =>
              (d.pass_items || []).some((item: any) =>
                (item.event?.id || item.event_id) == this.event.id
              )
            );

          if (!discount) {
            this.showNoPassError();
            return;
          }

          this.abono = discount;
          this.listEvents = (discount.pass_items || [])
            .map((item: any) => item.event)
            .filter((e: any) => !!e);

          // Verificar si hay eventos antes de calcular sesiones
          if (this.listEvents.length > 0) {
            // Calcular la próxima sesión disponible para cada evento
            const sessionCalculations = this.listEvents.map(event =>
              this.calculateNextAvailableSession(event)
            );

            // Esperar a que todas las sesiones se calculen antes de mostrar
            forkJoin(sessionCalculations).subscribe({
              next: () => {
                this.loaded = true;
                this.startSingleSeatPurchase();
              },
              error: (err) => {
                console.error('Error al calcular sesiones:', err);
                this.loaded = true; // Mostrar de todos modos
                this.startSingleSeatPurchase();
              }
            });
          } else {
            this.showNoPassError();
          }
        },
        error: () => {
          this.showNoPassError();
        }
      })
  }

  showNoPassError() {
    this.dialogRef.close();
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: true,
        text: 'Este abono no tiene eventos disponibles en este momento. Por favor, contacta con el organizador.',
        btnCancel: false
      }
    });
  }

  calculateNextAvailableSession(event: any) {
    // Obtener el evento completo con sus sesiones
    return this.eventService.getEventById(event.id).pipe(
      map((res) => {
        const fullEvent = res.data;
        if (fullEvent.sessions && fullEvent.sessions.length > 0) {
          // Filtrar solo sesiones futuras
          const now = new Date();
          now.setHours(0, 0, 0, 0);

          const futureSessions = fullEvent.sessions.filter((session: any) => {
            const sessionDate = new Date(session.start_date);
            sessionDate.setHours(0, 0, 0, 0);
            return sessionDate >= now;
          }).sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

          // Establecer la próxima sesión disponible
          if (futureSessions.length > 0) {
            event.next_session = futureSessions[0].start_date;
          } else {
            event._isFinished = true;
          }
        } else {
          event._isFinished = true;
        }
        return event;
      }),
      catchError((err) => {
        console.error('Error al obtener sesiones del evento:', event.id, err);
        return of(event); // Retornar el evento sin modificar en caso de error
      })
    );
  }

  isPastEvent(event: any): boolean {
    return !!event._isFinished || event.active === false;
  }

  // --- Abono de butaca única ---

  changePassQuantity(delta: number) {
    const quantity = this.passQuantity + delta;
    if (quantity < 1 || quantity > 10 || this.openingMap) return;
    this.passQuantity = quantity;
    // La selección era para otra cantidad de abonos
    this.passSeats = [];
    this.passSeatsEventId = null;
    this.passQuote = null;
  }

  /** Deshace la butaca única ofrecida y vuelve al recorrido evento a evento. */
  backToEventByEvent() {
    if (this.openingMap) return;
    this.sameSeatChosen = false;
    this.passQuantity = 1;
    this.passSeats = [];
    this.passSeatsEventId = null;
    this.passQuote = null;
  }

  /**
   * Tras elegir butaca en el primer evento: si está libre en todos los del
   * abono se ofrece llevársela a todos, y si el comprador acepta se pasa a la
   * compra en butaca única. En cualquier otro caso se guarda la selección
   * (`save`) y el recorrido evento a evento sigue como siempre.
   */
  private offerSameSeatOrSave(eventId: number, seats: any[], save: () => void) {
    const otherEventsChosen = this.ticketsSelected.some((sel: any) => sel.event != eventId);
    const canOffer = this.singleSeatPassService.canOffer(this.abono)
      && !otherEventsChosen
      && this.listEvents.length > 1
      && seats.length > 0
      && !this.listEvents.some(e => this.isPastEvent(e));

    if (!canOffer) {
      save();
      return;
    }

    this.loadingSelection = true;
    this.singleSeatPassService.offerSameSeat(this.abono, eventId, seats.map((s: any) => s.id)).subscribe((quote) => {
      this.loadingSelection = false;
      if (!quote) {
        save();
        return;
      }
      this.passQuantity = seats.length;
      this.passSeats = seats.map((s: any) => ({ id: s.id, row: s.row, seat: s.seat, section: s.section?.name || null }));
      this.passSeatsEventId = eventId;
      this.passQuote = quote;
      this.sameSeatChosen = true;
    });
  }

  /** Igual que en un abono normal: con un evento finalizado no se puede comprar. */
  get singleSeatBlocked(): boolean {
    return this.listEvents.some(e => this.isPastEvent(e));
  }

  /** Butaca única obligatoria aún sin butaca: este diálogo solo se ve con la butaca elegida. */
  get awaitingSeats(): boolean {
    return this.singleSeatRequired && !this.singleSeatBlocked && !this.passSeats.length;
  }

  /** Sin butaca no hay nada que enseñar aquí: se cierra todo. */
  private closeIfAwaitingSeats() {
    if (this.awaitingSeats) {
      this.dialogRef.close();
    }
  }

  /**
   * Abono de butaca única: igual que en la web, primero cuántos abonos y de
   * ahí directo al mapa.
   */
  private startSingleSeatPurchase() {
    if (!this.awaitingSeats) return;
    this.dialogRef.addPanelClass('dialog-behind-map');
    this.dialog.open(PassSelectDialogComponent, {
      panelClass: ['info-dialog', 'pass-select-dialog-panel'],
      data: { passes: [this.abono] }
    }).afterClosed().subscribe((result: any) => {
      this.dialogRef.removePanelClass('dialog-behind-map');
      if (!result?.pass) {
        this.close();
        return;
      }
      this.passQuantity = result.quantity || 1;
      this.openPassSeatMap();
    });
  }

  openPassSeatMap() {
    // Mismo candado que openSelectEvent: un segundo toque en el tótem abriría
    // un segundo mapa en blanco.
    if (this.openingMap || this.selectingEvent || this.singleSeatBlocked) return;
    this.openingMap = true;
    this.selectingEvent = true;
    this.loadingSelection = true;

    const hideDialog = this.awaitingSeats;
    this.singleSeatPassService.pickSeats(this.abono, this.passQuantity, () => {
      this.loadingSelection = false;
      if (hideDialog) this.dialogRef.addPanelClass('dialog-behind-map');
    }).pipe(
      switchMap((seats) => {
        if (!seats) return of(null);
        this.loadingSelection = true;
        this.dialogRef.removePanelClass('dialog-behind-map');
        return this.bondsService.quotePassSeats(this.abono.id, seats.map(s => s.id)).pipe(
          map((res: any) => ({ seats, quote: res.data }))
        );
      })
    ).subscribe({
      next: (result) => {
        this.openingMap = false;
        this.selectingEvent = false;
        this.loadingSelection = false;
        this.dialogRef.removePanelClass('dialog-behind-map');
        if (result) {
          this.passSeats = result.seats;
          this.passSeatsEventId = null;
          this.passQuote = result.quote;
        } else {
          this.closeIfAwaitingSeats();
        }
      },
      error: (err) => {
        this.openingMap = false;
        this.selectingEvent = false;
        this.loadingSelection = false;
        this.dialogRef.removePanelClass('dialog-behind-map');
        this.onSingleSeatError(err);
      }
    });
  }

  /**
   * Si otra persona se ha quedado la butaca entre medias, el back no añade
   * nada: se avisa, se descarta la selección y se reabre el mapa, que se pide
   * de nuevo y ya sale actualizado.
   */
  private onSingleSeatError(err: any) {
    const seatsTaken = err.status === 409 && err.error?.errors?.code === 'seats_unavailable';
    if (seatsTaken) {
      this.passSeats = [];
      this.passSeatsEventId = null;
      this.passQuote = null;
    }
    const many = this.passQuantity > 1;
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: !seatsTaken,
        text: seatsTaken
          ? `${many ? 'Alguna de las butacas que habías elegido' : 'La butaca que habías elegido'} acaba de ocuparse en uno de los eventos del abono. Elige ${many ? 'de nuevo' : 'otra'} en el mapa.`
          : (err.error?.errors?.errors?.[0] || err.error?.message || 'Error al añadir el abono al carrito'),
        btnCancel: false
      }
    });
    if (seatsTaken) {
      dialogRef.afterClosed().subscribe(() => this.openPassSeatMap());
    } else {
      this.closeIfAwaitingSeats();
    }
  }

  openSelectEvent(event: any) {
    // En un abono de butaca única los eventos son solo informativos
    if (this.isSingleSeat) {
      return;
    }
    let past = this.isPastEvent(event);
    if (!past) {
      // Candado exclusivo del tótem: entre el clic y el mapa hay dos llamadas
      // asíncronas, y en pantalla táctil un segundo toque abría un segundo
      // selector de butacas que salía en blanco (fabric se engancha al id fijo
      // #myCanvas y solo pinta en el primero del DOM). Se coge en el clic y no
      // se suelta hasta cerrar el diálogo. En web, todo como siempre.
      if (this.totemActive && this.selectingEvent) {
        return;
      }
      if (!this.openingMap) {
        this.openingMap = true;
        if (this.totemActive) {
          this.selectingEvent = true;
          this.loadingSelection = true;
        }
        // Obtener el evento completo con sus sesiones
        this.eventService.getEventById(event.id).subscribe({
          next: (res) => {
            const fullEvent = res.data;
            this.openingMap = false;

            // Verificar que tiene sesiones
            if (fullEvent.sessions && fullEvent.sessions.length > 0) {
              // Filtrar solo sesiones futuras
              const now = new Date();
              now.setHours(0, 0, 0, 0);

              const futureSessions = fullEvent.sessions.filter((session: any) => {
                const sessionDate = new Date(session.start_date);
                return sessionDate >= now;
              });

              if (futureSessions.length === 0) {
                // No hay sesiones futuras
                this.selectingEvent = false;
                this.loadingSelection = false;
                this.dialog.open(InfoCardComponent, {
                  panelClass: 'info-dialog',
                  data: {
                    icon: true,
                    text: 'Este evento no tiene sesiones futuras disponibles',
                    btnCancel: false
                  }
                });
                return;
              }

              // Actualizar el evento con solo sesiones futuras
              fullEvent.sessions = futureSessions;

              // Abrir el selector de sesión y entrada en el mismo modal
              this.openTicketSelector(fullEvent, event);
            } else {
              this.selectingEvent = false;
              this.loadingSelection = false;
              this.dialog.open(InfoCardComponent, {
                panelClass: 'info-dialog',
                data: {
                  icon: true,
                  text: 'Este evento no tiene sesiones disponibles',
                  btnCancel: false
                }
              });
            }
          },
          error: (err) => {
            this.openingMap = false;
            this.selectingEvent = false;
            this.loadingSelection = false;
            console.error('Error al cargar el evento:', err);
          }
        });
      }
    }
  }

  openTicketSelector(fullEvent: any, originalEvent: any) {
    // Primero intentar obtener el mapa para saber si el evento tiene asientos numerados
    let sessionSelection = fullEvent.use_map ? true : false;

    // Si el evento tiene mapa y solo hay una sesión futura, ir directamente al mapa
    // sin abrir el dialog intermedio de selección de sesión
    if (sessionSelection && fullEvent.sessions.length === 1) {
      this.openMapForSession(fullEvent, fullEvent.sessions[0], originalEvent);
      return;
    }

    const dialogRef = this.dialog.open(HoursDatesEventComponent, {
      panelClass: ['info-dialog', 'not-padding', 'panel-seats', 'abono-selector'],
      data: {
        event: fullEvent,
        organizer: false,
        fromAbono: true,
        abono: this.abono,
        sessionSelectionOnly: sessionSelection
      }
    });
    this.loadingSelection = false;

    if (sessionSelection) {
      dialogRef.afterClosed().subscribe(result => {
        if (result && result.session) {
          // El candado sigue cogido: la cadena continúa con el mapa de butacas.
          this.loadingSelection = true;
          this.openMapForSession(fullEvent, result.session, originalEvent);
        } else {
          this.selectingEvent = false;
        }
      });
    } else {
      dialogRef.afterClosed().subscribe(result => {
        this.selectingEvent = false;
        if (result && result.cart_lines?.length > 0) {
          this.saveTicketSelection(fullEvent, result.session, result.cart_lines, result.price, originalEvent, result.tickets);
        }
      });
    }
  }

  openMapForSession(fullEvent: any, session: any, originalEvent: any) {
    // El evento tiene mapa - Abrir SeatsPanelComponent para seleccionar asientos
    this.eventService.getMapEventBySession(fullEvent.id, session.id).subscribe({
      next: (res: any) => {
        this.openingMap = false;
        const tickets = fullEvent.tickets.filter((ticket: any) => {
          return !ticket.only_tpv_sales && ticket.active && (!ticket.id_session || ticket.id_session == session.id);
        });

        const dialogRef = this.dialog.open(SeatsPanelComponent, {
          panelClass: ['info-dialog', 'not-padding', 'panel-seats'],
          data: {
            event: fullEvent,
            session: session,
            mapEvent: res.data,
            tickets: tickets,
            fromAbono: true,
            abono: this.abono
          }
        });

        this.loadingSelection = false;

        dialogRef.afterClosed().subscribe(data => {
          this.selectingEvent = false;

          if (data && data.cart_lines?.length > 0) {

            // Guardar la selección para este evento
            // Para eventos con mapa, pasar los asientos completos
            this.offerSameSeatOrSave(fullEvent.id, data.seats || [], () =>
              this.saveTicketSelection(fullEvent, session, data.cart_lines, data.price, originalEvent, data.seats)
            );
          }
        });
      },
      error: (err: any) => {
        this.openingMap = false;
        console.error('Error al obtener el mapa del evento:', err);

        // Si la sesión no tiene mapa, abrir el selector simple de tickets
        if (err.status === 404) {
          // Crear una copia del evento sin map_version para que muestre el selector simple
          const eventWithoutMap = { ...fullEvent, map_version: null };

          const dialogRef = this.dialog.open(HoursDatesEventComponent, {
            panelClass: ['info-dialog', 'not-padding', 'panel-seats', 'abono-selector'],
            data: {
              event: eventWithoutMap,
              organizer: false,
              fromAbono: true,
              abono: this.abono,
              preselectedSession: session // Pasar la sesión preseleccionada
            }
          });

          this.loadingSelection = false;

          dialogRef.afterClosed().subscribe(result => {
            this.selectingEvent = false;
            if (result && result.cart_lines?.length > 0) {
              this.saveTicketSelection(fullEvent, result.session, result.cart_lines, result.price, originalEvent, result.tickets);
            }
          });
        } else {
          // Otro tipo de error
          this.selectingEvent = false;
          this.loadingSelection = false;
          this.dialog.open(InfoCardComponent, {
            panelClass: 'info-dialog',
            data: {
              icon: true,
              text: 'Error al cargar el mapa del evento. Por favor, inténtalo de nuevo.',
              btnCancel: false
            }
          });
        }
      }
    });
  }

  saveTicketSelection(fullEvent: any, session: any, cartLines: any[], price: number, originalEvent: any, ticketDetails?: any) {

    let index = this.ticketsSelected.findIndex((value: any) => value.event == fullEvent.id);
    const payload = {
      event: fullEvent.id,
      session: session.id,
      tickets: cartLines,
      price: price
    };

    if (index != -1) {
      this.ticketsSelected[index] = payload;
    } else {
      this.ticketsLength++;
      this.ticketsSelected.push(payload);
    }
    // Actualizar el evento para mostrar que ya tiene entrada seleccionada
    if (ticketDetails && ticketDetails[0]) {
      const firstItem = ticketDetails[0];

      // Verificar si es un ticket simple (sin mapa) o un asiento (con mapa)
      if (firstItem.ticket && firstItem.number) {
        // Evento sin mapa - crear estructura compatible con HTML
        originalEvent.ticket = {
          option: {
            ticket: firstItem.ticket  // Estructura anidada que espera el HTML
          },
          quantity: firstItem.number,
          isSimpleTicket: true // Flag para identificar tipo de ticket
        };
      } else if (firstItem.section) {
        // Evento con mapa - usar el asiento completo
        originalEvent.ticket = firstItem;
      }
    } else {
      // Fallback - usar cartLines (no debería llegar aquí)
      originalEvent.ticket = cartLines[0];
    }

    // Guardar la información de la sesión para mostrarla
    originalEvent.selectedSession = session;
    originalEvent.selected = true;
  }

  getDateTime(date, hour) {
    let dateAux = new Date(date);
    let time = null;
    if (hour) {
      let index = hour.indexOf(':');
      dateAux.setUTCHours(hour.substring(0, index));
      dateAux.setUTCMinutes(hour.substring(index + 1));

      time = dateAux.toISOString();
    }
    return { date: date, time: time };
  }

  goPay(exit: boolean = true) {
    let user = this.auth.getProfile();
    if (!user) {
      // En el tótem no se pide login: la compra cuelga del usuario del kiosco.
      if (AppSettings.ENV_TOTEM) {
        this.auth.ensureTotemSession().subscribe((totemUser) => {
          if (totemUser) {
            this.goPay(exit);
          } else {
            this.dialog.open(InfoCardComponent, {
              panelClass: 'info-dialog',
              data: {
                icon: true,
                text: '¡Vaya! No se ha podido iniciar la compra. Por favor, vuelve a intentarlo.',
                btnCancel: false
              }
            });
          }
        });
        return;
      }
      const dialogRef = this.dialog.open(LoginComponent, {
        panelClass: ['info-dialog', 'dialog-login'],
        data: {
          exit: false,
          seats: true
        }
      });
      return;
    }
    if (user && !this.openingMap && this.readyToPay) {
      this.openingMap = true;
      this.isSingleSeat ? this.processSingleSeatPass(exit) : this.processAbonoWithCart(exit);
    }
  }

  processSingleSeatPass(exit: boolean) {
    const payload = {
      id_discount: parseInt(this.abono.id),
      seat_ids: this.passSeats.map(s => s.id),
      event_id: this.passSeatsEventId,
    };

    this.shoppingService.addSingleSeatPass(payload).subscribe({
      next: () => {
        this.dialogRef.close({ data: payload, exit: exit });
        this.openingMap = false;
      },
      error: (err) => {
        this.openingMap = false;
        this.onSingleSeatError(err);
      }
    });
  }

  processAbonoWithCart(exit: boolean) {
    const cartLines = [];
    const discountId = parseInt(this.abono.id);
    this.ticketsSelected.forEach(selection => {
      selection.tickets.forEach(ticket => {

        const cartLine: any = {
          id_event: parseInt(selection.event),
          id_session: parseInt(selection.session),
          // En precio fijo puede venir sin entrada: la elige el back
          id_ticket: ticket.id_ticket ? parseInt(ticket.id_ticket) : null,
          quantity: ticket.quantity || 1,
          id_discount: discountId,
        };

        // Si el ticket tiene id_seat (evento con mapa), incluirlo
        if (ticket.id_seat) {
          cartLine.id_seat = parseInt(ticket.id_seat);
        }

        cartLines.push(cartLine);
      });
    });

    const payload = {
      cart_lines: cartLines,
      id_discount: discountId
    };

    this.shoppingService.addBondToCard(payload).subscribe({
      next: (response) => {
        this.dialogRef.close({ data: payload, exit: exit });
        this.openingMap = false;
      },
      error: (err) => {
        this.openingMap = false;
        if (err.status == 400 && err.error.tickets) {
          this.dialog.open(InfoCardComponent, {
            panelClass: 'info-dialog',
            data: {
              icon: false,
              text: err.error.msg,
              btnCancel: false
            }
          });
        } else {
          this.dialog.open(InfoCardComponent, {
            panelClass: 'info-dialog',
            data: {
              icon: true,
              text: err.error?.message || 'Error al añadir el abono al carrito',
              btnCancel: false
            }
          });
        }
      }
    });
  }

  close() {
    this.dialogRef.close();
  }

}
